import React, { useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { FloatingCta } from '../components/FloatingCta';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { useDeadlineCountdown } from '../hooks/useDeadlineCountdown';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Flame, AlertTriangle, ChevronDown, XCircle, ShieldCheck, Zap, ArrowRight, Clock } from 'lucide-react';
const bonuses = [{
  title: 'LEAD SCRAPER BRAIN',
  value: '$997',
  desc: 'Encuentra negocios sin website en segundos.',
  bullets: ['teléfonos', 'emails', 'Instagram', 'WhatsApp'],
  footer: 'Listos para prospectar.',
  image: 'https://cdn.magicpatterns.com/uploads/dJfzTiuQYwUsv1t3US9VQy/03_bonus_1_lead_scraper.png'
}, {
  title: 'CUSTOM LLOYD AI CLOSER',
  value: '$497',
  desc: 'Tu asistente IA para responder objeciones y ayudarte a cerrar clientes.',
  bullets: ['copiar', 'pegar', 'enviar'],
  footer: 'Perfecto para personas sin experiencia en ventas.',
  image: 'https://cdn.magicpatterns.com/uploads/7BgYBgLPT2w2cJst6QECLC/bonus_2_custom_lloyd_FINAL.png'
}, {
  title: 'COMUNIDAD PRIVADA DE POR VIDA',
  value: '$497',
  desc: 'Acceso a la comunidad privada con:',
  bullets: ['soporte', 'actualizaciones', 'networking', 'ayuda en tiempo real'],
  footer: 'No estarás solo.',
  image: 'https://cdn.magicpatterns.com/uploads/w6XAsPTLjY4pq8ASqgBepN/05_bonus_3_community.png'
}, {
  title: 'GENERADOR DE PROPUESTAS CON IA',
  value: '$997',
  desc: 'Crea propuestas profesionales en minutos. Incluye:',
  bullets: ['propuestas listas', 'tablas de inversión', 'estructura profesional', 'automatización'],
  footer: 'Pasa de llamada → propuesta → cierre.',
  image: 'https://cdn.magicpatterns.com/uploads/pFmy6QjoBuztNTpkenBv4P/06_bonus_4_proposal_generator.png'
}, {
  title: 'STACK DE RECURSOS PRO',
  value: '$497',
  desc: 'Herramientas, plantillas y recursos listos para ejecutar.',
  bullets: [],
  image: 'https://cdn.magicpatterns.com/uploads/6ZL6ezBjhVx7ibUARCKVp1/07_bonus_5_resources_stack_FINAL_CINEMATIC.png'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export function OfferPage() {
  const {
    trackCustomEvent
  } = useMetaPixel();
  const countdown = useDeadlineCountdown();
  const CHECKOUT_URL = 'https://www.fanbasis.com/agency-checkout/true-marketer/KA5Pz';
  useEffect(() => {
    trackCustomEvent('Offer-Page-Visit');
  }, [trackCustomEvent]);
  const handleCheckout = () => {
    trackCustomEvent('Initiate-Checkout-297');
    window.location.href = CHECKOUT_URL;
  };
  const pad = (n: number) => String(n).padStart(2, '0');
  // CTA block: either the buy button or an "offer ended" notice
  const CtaBlock = ({
    className = ''
  }: {
    className?: string;
  }) => countdown.isExpired ? <div className={`mx-auto max-w-md bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl p-6 space-y-2 ${className}`} data-id="element-88">
        <p className="text-lg font-semibold text-white" data-id="element-89">
          La oferta especial ha terminado
        </p>
        <p className="text-sm text-white/60" data-id="element-90">
          Contáctanos por WhatsApp para conocer las opciones disponibles.
        </p>
      </div> : <div className={className} data-id="element-91">
        <Button onClick={handleCheckout} size="lg" className="w-full sm:w-auto sm:min-w-[320px] text-base sm:text-xl font-bold h-16 sm:h-20 shadow-[0_0_50px_-12px_rgba(16,185,129,0.5)] ring-1 ring-emerald-400/40" rightIcon={<ArrowRight className="w-6 h-6" data-id="element-93" />} data-id="element-92">
          ACTIVA TU ACCESO AHORA
        </Button>
      </div>;
  return <div className="min-h-screen w-full bg-[#050505] text-white font-sans selection:bg-emerald-500/30 relative overflow-hidden" data-id="element-94">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-95">
        <motion.div animate={{
        x: [0, 50, -30, 0],
        y: [0, -40, 60, 0],
        scale: [1, 1.1, 0.9, 1]
      }} transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-emerald-500/10 rounded-full blur-[120px]" data-id="element-96" />
        <motion.div animate={{
        x: [0, -60, 40, 0],
        y: [0, 50, -30, 0],
        scale: [1, 0.9, 1.1, 1]
      }} transition={{
        duration: 22,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-emerald-400/5 rounded-full blur-[120px]" data-id="element-97" />
      </div>

      {/* Top Banner */}
      <div className="relative z-10 bg-red-500/10 backdrop-blur-xl border-b border-red-500/20 text-red-300 py-3 px-4 text-center flex items-center justify-center gap-2 font-medium text-sm tracking-wide" data-id="element-98">
        <AlertCircle className="w-4 h-4" data-id="element-99" />
        {countdown.isExpired ? 'OFERTA ESPECIAL CERRADA' : 'ACCESO RESERVADO POR TIEMPO LIMITADO'}
      </div>

      <main className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 py-12 sm:py-16 space-y-20 sm:space-y-24" data-id="element-100">
        {/* Hero Section */}
        <motion.section initial="hidden" animate="visible" variants={fadeUp} className="text-center space-y-8 sm:space-y-10" data-id="element-101">
          <div className="space-y-4" data-id="element-102">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-semibold tracking-tight text-white leading-[1.1]" data-id="element-103">
              <span className="block bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent" data-id="element-104">
                AI WEB CLOSER
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/60 max-w-2xl mx-auto leading-relaxed font-medium" data-id="element-105">
              El sistema completo para vender páginas web con IA a negocios
              locales… incluso si empiezas desde cero.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-2 md:p-4 shadow-2xl shadow-black/50" data-id="element-106">
            <img src="https://cdn.magicpatterns.com/uploads/g4SAjMpoXz3BM6AyA6VfjW/AI_WEB_CLOSER.png" alt="AI Web Closer System" className="w-full rounded-2xl border border-white/10" data-id="element-107" />
          </div>

          {/* Deadline Countdown */}
          {!countdown.isExpired && <div className="bg-red-500/10 backdrop-blur-2xl border border-red-500/20 rounded-3xl p-5 sm:p-6 md:p-8 max-w-xl mx-auto" data-id="element-108">
              <div className="flex items-center justify-center gap-2 text-red-300 text-[11px] sm:text-xs uppercase tracking-widest font-semibold mb-4 text-center" data-id="element-109">
                <Clock className="w-4 h-4 shrink-0" data-id="element-110" />
                El precio sube a $1997 el viernes en
              </div>
              <div className="flex justify-center items-baseline gap-1.5 sm:gap-3 md:gap-4" data-id="element-111">
                {countdown.days > 0 && <>
                    <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-112">
                      <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-113">
                        {pad(countdown.days)}
                      </span>
                      <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-114">
                        Días
                      </span>
                    </div>
                    <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-red-300/40" data-id="element-115">
                      :
                    </span>
                  </>}
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-116">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-117">
                    {pad(countdown.hours)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-118">
                    Hrs
                  </span>
                </div>
                <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-red-300/40" data-id="element-119">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-120">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-121">
                    {pad(countdown.minutes)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-122">
                    Min
                  </span>
                </div>
                <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-red-300/40" data-id="element-123">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[70px]" data-id="element-124">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tabular-nums tracking-tight" data-id="element-125">
                    {pad(countdown.seconds)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-red-300/80 mt-1" data-id="element-126">
                    Seg
                  </span>
                </div>
              </div>
            </div>}

          <div className="pt-4" data-id="element-127">
            {countdown.isExpired ? <CtaBlock data-id="element-128" /> : <Button onClick={handleCheckout} size="lg" className="w-full sm:w-auto sm:min-w-[320px] text-base sm:text-lg font-bold h-16 shadow-[0_0_40px_-10px_rgba(16,185,129,0.4)] ring-1 ring-emerald-400/30" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-130" />} data-id="element-129">
                ACTIVA TU ACCESO AHORA
              </Button>}
          </div>
        </motion.section>

        {/* Core Offer */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl shadow-2xl shadow-black/50 border border-white/10 p-6 sm:p-8 md:p-12 relative overflow-hidden" data-id="element-131">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-132" />
          <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-10 border-b border-white/10 pb-6 sm:pb-8" data-id="element-133">
            <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400 shrink-0" data-id="element-134" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-135">
              LO QUE ACTIVAS HOY
            </h2>
          </div>

          <div className="space-y-6 sm:space-y-8" data-id="element-136">
            <div data-id="element-137">
              <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" data-id="element-138">
                AI WEB CLOSER SYSTEM™
              </h3>
              <p className="text-emerald-400/80 font-medium mt-1" data-id="element-139">
                Valor $1,997
              </p>
            </div>
            <p className="text-white/70 text-base sm:text-lg" data-id="element-140">
              El sistema paso a paso que te muestra cómo:
            </p>
            <ul className="space-y-4 sm:space-y-5" data-id="element-141">
              {['Encontrar negocios que NECESITAN websites', 'Crear páginas web con IA en aproximadamente 10 minutos', 'Conseguir clientes usando mensajes simples', 'Cerrar clientes por WhatsApp', 'Cobrar entre $300 y $1,500 por proyecto', 'Escalar usando automatización e IA'].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-142">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 shrink-0 mt-0.5" data-id="element-143" />
                  <span className="text-white/80 text-base sm:text-lg" data-id="element-144">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.section>

        {/* Not Just a Course */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeUp} className="text-center space-y-6 sm:space-y-8 bg-white/[0.02] backdrop-blur-3xl rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl border border-white/5 relative overflow-hidden" data-id="element-145">
          <div className="absolute inset-0 bg-emerald-500/5 blur-3xl" data-id="element-146" />
          <div className="relative z-10" data-id="element-147">
            <div className="flex justify-center mb-6" data-id="element-148">
              <Flame className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500/80" data-id="element-149" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight mb-4" data-id="element-150">
              ESTO NO ES SOLO UN CURSO
            </h2>
            <p className="text-lg sm:text-xl text-white/60 mb-6" data-id="element-151">
              Estás activando:
            </p>
            <p className="text-lg sm:text-xl md:text-2xl font-medium leading-relaxed text-emerald-400" data-id="element-152">
              un sistema • herramientas • automatizaciones • prompts • scripts •
              estructuras • IA entrenada • y un proceso diseñado para reducir
              errores y acelerar resultados.
            </p>
          </div>
        </motion.section>

        {/* Bonuses */}
        <section className="space-y-10 sm:space-y-12" data-id="element-153">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-center mb-10 sm:mb-12" data-id="element-154">
            Tus Bonos de Acción Rápida
          </motion.h2>

          {bonuses.map((bonus, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl shadow-2xl shadow-black/50 border border-white/10 overflow-hidden" data-id="element-155">
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-156" />
              {/* Bonus Image */}
              <div className="w-full bg-black/40 border-b border-white/10 p-3 sm:p-4 md:p-8 flex justify-center" data-id="element-157">
                <img src={bonus.image} alt={bonus.title} className="w-full max-w-2xl rounded-2xl border border-white/10 shadow-2xl" data-id="element-158" />
              </div>

              {/* Bonus Content */}
              <div className="p-6 sm:p-8 md:p-10 space-y-5 sm:space-y-6" data-id="element-159">
                <div data-id="element-160">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight text-white mb-2" data-id="element-161">
                    BONUS #{i + 1} — {bonus.title}
                  </h3>
                  <p className="text-emerald-400 font-medium text-base sm:text-lg" data-id="element-162">
                    Valor: {bonus.value}
                  </p>
                </div>
                <p className="text-white/70 text-base sm:text-lg leading-relaxed" data-id="element-163">
                  {bonus.desc}
                </p>
                {bonus.bullets.length > 0 && <ul className="flex flex-wrap gap-2 sm:gap-3 pt-2" data-id="element-164">
                    {bonus.bullets.map((b, j) => <li key={j} className="flex items-center gap-2 bg-white/5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium text-white/80 border border-white/10" data-id="element-165">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-166" />
                        {b}
                      </li>)}
                  </ul>}
                {bonus.footer && <p className="text-white/50 italic pt-3 sm:pt-4 text-base sm:text-lg" data-id="element-167">
                    {bonus.footer}
                  </p>}
              </div>
            </motion.div>)}

          {/* Special Bonus */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="relative bg-white/[0.04] backdrop-blur-2xl rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl shadow-emerald-900/20 border border-emerald-500/30 overflow-hidden mt-10 sm:mt-12" data-id="element-168">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent" data-id="element-169" />
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" data-id="element-170" />

            <div className="absolute top-0 right-0 p-8 opacity-5" data-id="element-171">
              <Zap className="w-48 h-48" data-id="element-172" />
            </div>

            <div className="relative z-10 space-y-5 sm:space-y-6" data-id="element-173">
              <div className="inline-block bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider" data-id="element-174">
                Bonus Especial en Vivo
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-175">
                AI ADS CASH MACHINE™
              </h3>
              <p className="text-emerald-400 font-medium text-base sm:text-lg" data-id="element-176">
                Valor: $1,997
              </p>
              <p className="text-white/70 text-lg sm:text-xl leading-relaxed max-w-2xl" data-id="element-177">
                Aprende cómo ayudar negocios locales a conseguir clientes usando
                publicidad digital.
              </p>
              <p className="text-white/90 font-medium bg-white/5 p-5 sm:p-6 rounded-2xl border border-white/10 text-base sm:text-lg" data-id="element-178">
                Y lo más importante: cómo convertir clientes de websites… en
                clientes mensuales recurrentes.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Value Stack & Price */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-3xl rounded-3xl shadow-2xl shadow-emerald-900/20 border border-emerald-400/30 p-6 sm:p-8 md:p-16 text-center space-y-8 sm:space-y-10 relative overflow-hidden" data-id="element-179">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" data-id="element-180" />

          <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-emerald-500/20 backdrop-blur-md border border-emerald-500/30 border-t-0 text-emerald-300 px-5 sm:px-8 py-2 rounded-b-2xl font-bold text-xs sm:text-sm tracking-widest whitespace-nowrap" data-id="element-181">
            RESUMEN DE TU INVERSIÓN
          </div>

          <div className="pt-10 sm:pt-8 space-y-3" data-id="element-182">
            <p className="text-white/50 font-medium uppercase tracking-widest text-xs sm:text-sm" data-id="element-183">
              Valor Total
            </p>
            <p className="text-3xl sm:text-4xl font-semibold text-white/40 line-through decoration-red-500/50 decoration-4" data-id="element-184">
              $5,482+
            </p>
          </div>

          <div className="space-y-5 sm:space-y-6" data-id="element-185">
            <div className="flex justify-center" data-id="element-186">
              <Flame className="w-9 h-9 sm:w-10 sm:h-10 text-orange-500/80" data-id="element-187" />
            </div>
            <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs sm:text-sm" data-id="element-188">
              {countdown.isExpired ? 'Precio Regular' : 'Hoy Solo'}
            </p>
            <div className="text-6xl sm:text-7xl md:text-8xl font-semibold tracking-tight bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent" data-id="element-189">
              {countdown.isExpired ? '$1997' : '$297'}{' '}
              <span className="text-2xl sm:text-3xl text-white/40 font-medium" data-id="element-190">
                USD
              </span>
            </div>
          </div>

          <div className="pt-6 sm:pt-8" data-id="element-191">
            <CtaBlock data-id="element-192" />
          </div>
        </motion.section>

        {/* Important Note */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-amber-500/5 backdrop-blur-xl rounded-3xl p-6 sm:p-8 md:p-10 border border-amber-500/20 relative overflow-hidden" data-id="element-193">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" data-id="element-194" />
          <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6" data-id="element-195">
            <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400 shrink-0" data-id="element-196" />
            <h3 className="text-xl sm:text-2xl font-semibold text-amber-200 tracking-tight" data-id="element-197">
              IMPORTANTE
            </h3>
          </div>
          <p className="text-amber-100/80 text-lg sm:text-xl mb-6 sm:mb-8 leading-relaxed" data-id="element-198">
            No estás pagando por "información". Estás activando:
          </p>
          <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-199">
            {['un sistema', 'herramientas', 'automatización', 'IA', 'estructura', 'velocidad', 'claridad'].map((item, i) => <span key={i} className="bg-amber-500/10 text-amber-200 px-3 sm:px-4 py-2 rounded-full font-medium text-xs sm:text-sm border border-amber-500/20" data-id="element-200">
                {item}
              </span>)}
          </div>
        </motion.section>

        {/* Webinar Context */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-center space-y-6 sm:space-y-8 max-w-2xl mx-auto py-4 sm:py-8" data-id="element-201">
          <div className="flex justify-center" data-id="element-202">
            <ChevronDown className="w-9 h-9 sm:w-10 sm:h-10 text-white/20 animate-bounce" data-id="element-203" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white" data-id="element-204">
            SI ESTO TE HIZO SENTIDO EN EL WEBINAR…
          </h3>
          <p className="text-lg sm:text-xl text-white/60" data-id="element-205">
            Entonces ya sabes algo: El problema nunca fue tu esfuerzo.
          </p>
          <p className="text-2xl sm:text-3xl font-semibold bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent" data-id="element-206">
            Era NO tener un sistema claro.
          </p>
        </motion.section>

        {/* Who it's for / Not for */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="grid md:grid-cols-2 gap-6 sm:gap-8" data-id="element-207">
          <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-208">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-209" />
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8" data-id="element-210">
              <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400 shrink-0" data-id="element-211" />
              <h3 className="text-lg sm:text-xl font-semibold text-white tracking-tight" data-id="element-212">
                ESTE SISTEMA ES PARA TI SI:
              </h3>
            </div>
            <ul className="space-y-4 sm:space-y-5" data-id="element-213">
              {['Quieres ingresos online reales', 'Quieres vender servicios simples', 'Quieres aprovechar la IA', 'Quieres evitar modelos complicados', 'Quieres una oportunidad con alta demanda', 'Estás dispuesto a ejecutar'].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-214">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400/80 shrink-0 mt-0.5" data-id="element-215" />
                  <span className="text-white/80 text-base sm:text-lg" data-id="element-216">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-217">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-218" />
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8" data-id="element-219">
              <XCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400 shrink-0" data-id="element-220" />
              <h3 className="text-lg sm:text-xl font-semibold text-white tracking-tight" data-id="element-221">
                ESTE SISTEMA NO ES PARA TI SI:
              </h3>
            </div>
            <ul className="space-y-4 sm:space-y-5" data-id="element-222">
              {['Buscas dinero mágico', 'No quieres ejecutar', 'Quieres resultados sin acción', 'No estás dispuesto a aprender algo nuevo'].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-223">
                  <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-400/80 shrink-0 mt-0.5" data-id="element-224" />
                  <span className="text-white/80 text-base sm:text-lg" data-id="element-225">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.section>

        {/* Guarantee */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-8 sm:p-10 md:p-16 border border-white/10 text-center space-y-6 sm:space-y-8 relative overflow-hidden" data-id="element-226">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-227" />
          <div className="absolute inset-0 bg-emerald-500/5 blur-3xl" data-id="element-228" />
          <div className="relative z-10" data-id="element-229">
            <div className="flex justify-center mb-6" data-id="element-230">
              <div className="relative" data-id="element-231">
                <div className="absolute inset-0 bg-emerald-500/30 blur-xl rounded-full" data-id="element-232" />
                <ShieldCheck className="w-16 h-16 sm:w-20 sm:h-20 text-emerald-400 relative z-10" data-id="element-233" />
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white tracking-tight mb-5 sm:mb-6" data-id="element-234">
              GARANTÍA DE 30 DÍAS
            </h2>
            <p className="text-lg sm:text-xl text-white/70 max-w-xl mx-auto leading-relaxed" data-id="element-235">
              Prueba AI WEB CLOSER™ sin riesgo. Tu acceso está protegido con
              garantía de satisfacción de 30 días.
            </p>
          </div>
        </motion.section>

        {/* Final CTA */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-center space-y-8 sm:space-y-10 pb-12 sm:pb-16" data-id="element-236">
          <div className="flex justify-center" data-id="element-237">
            <Zap className="w-14 h-14 sm:w-16 sm:h-16 text-emerald-400/80" data-id="element-238" />
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white" data-id="element-239">
            TU OPORTUNIDAD ES AHORA
          </h2>
          <div className="text-lg sm:text-xl md:text-2xl text-white/60 space-y-3 max-w-2xl mx-auto leading-relaxed" data-id="element-240">
            <p data-id="element-241">Millones de negocios todavía necesitan websites.</p>
            <p data-id="element-242">La IA está cambiando el mercado rápidamente.</p>
            <p className="font-semibold text-white pt-4 sm:pt-6" data-id="element-243">
              Y las personas que aprendan esto primero… van a tener ventaja.
            </p>
          </div>

          <div className="pt-8 sm:pt-10" data-id="element-244">
            <CtaBlock data-id="element-245" />
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 pb-8 text-center text-white/40 text-sm" data-id="element-246">
        &copy; {new Date().getFullYear()} 48 Hrs AI Cash Machine. Todos los
        derechos reservados.
      </footer>

      {!countdown.isExpired && <FloatingCta onClick={handleCheckout} price="$297" originalPrice="$1997" label="ACTIVAR" data-id="element-247" />}

      <FloatingWhatsApp link="https://wa.link/infocommerce" data-id="element-248" />
    </div>;
}